import { mergeMap } from 'rxjs/operators';
import { take } from 'rxjs/operators';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  Validators
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { takeWhile } from 'rxjs/operators';

import {
  ErrorhandlerService,
  NotificationsService,
  TokenService,
  CreateOptionDataService,
  DateFormatService,
  FileReaderService
} from 'app/core/services';
import {
  checkIfEndDateIsLessThanStartDate,
  checkIfXinDescription, checkMaxLength,
  checkWhiteSpace
} from 'app/core/validators/custom.validator';
import { COMMON_DATETIMEPICKER_SETTINGS, COMMON_SELECT_SETTINGS } from 'app/shared/data';
import { ValidationMessage } from 'app/shared/interface';
import { DateToString } from 'app/shared/pipes';
import {
  Assembly_Poll_Validation_Messages,
  SpecialEvent,
  SpecialPoll,
  PollService
} from 'app/features/poll';
import { environment } from 'environments/environment';
import { MbscDatepickerOptions } from '@mobiscroll/angular';
import { DatepickerBase } from '@mobiscroll/angular/dist/js/core/components/datepicker/datepicker';

@Component({
  selector: 'coop-assembly-poll-create-edit',
  templateUrl: './assembly-poll-create-edit.component.html',
  styleUrls: ['./assembly-poll-create-edit.component.scss'],
  providers: [DateToString]
})
export class AssemblyPollCreateEditComponent implements OnInit, OnDestroy {
  pageTitle: string = 'Opret afstemning';
  previousUrl: string;
  assemblyPollForm: FormGroup;
  isProcessing: boolean = false;
  formSaved: boolean = false;
  currentUrl: string;
  parentUrl: string = '/bestyrelse';
  successUrl: string;
  routeId: string;
  imageUrl = {
    largeImage: 'assets/images/camera@3x.jpg',
    thumbnail: 'assets/images/camera@3x.jpg'
  };
  hasImage: boolean = false;
  editMode: boolean = false;
  assemblyEventId: string;
  showDeleteBox: boolean = false;
  showMsg: boolean = false;
  isActive: boolean = true;
  pollStarted: boolean = false;
  pollEnded: boolean = false;
  hasVoted: boolean = false;
  showResults: boolean = false;
  showXinDescError: boolean = false;
  showDurationPopup: boolean = false;
  answerCount: number = 0;
  candidateList: Array<string> = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
  colors = ['shade1', 'shade2', 'shade3', 'shade4', 'shade5'];
  assemblyEventList: SpecialEvent[];
  assemblyPoll: SpecialPoll;
  eventRegisterEnd: string;
  downloadUrl: string;
  downloadFile = {
    name: '',
    file: null
  };
  origin: string = '';
  canEdit: boolean = true;
  memberIdNotMatched = false;
  memberId: string = '';
  parentMemberId: string = '';
  electionEntriesPickerSettings = {
    ...COMMON_SELECT_SETTINGS, ...{
      data: Array.apply(null, Array(11))
        .map((_: any, i: any) => i + ' kryds')
        .splice('1')
    }
  };
  datePickerSettings: MbscDatepickerOptions = {
    ...COMMON_DATETIMEPICKER_SETTINGS, ...{
      dateWheels: 'YYYY MMMM DD',
      dateFormat: 'YYYY-MM-DD',
      stepMinute: 15,
      onOpen: (_, inst) => this._setupDateTime(inst),
      onClose: (_, inst) => this._initDurationPopup(inst)
    }
  };
  validationMessages: ValidationMessage = Assembly_Poll_Validation_Messages;
  visible: boolean = false;
  private _subscriptionState: boolean = true;

  // *** Check if Poll start date is after event registration end date *** //
  // @params [c : FormControl] => Start date
  // @return [Object or null] => If
  private _checkPollStart(
    c: AbstractControl
  ): { [key: string]: { [key: string]: boolean } } | null {
    const startDate = moment(c.get('startDate').value).toDate();
    const expiryDate = moment(this.eventRegisterEnd).toDate();
    if (expiryDate > startDate) {
      return {
        dateMismatchStartExpire: {
          mismatch: true
        }
      };
    }
    return null;
  }

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _formBuilder: FormBuilder,
    private _dateToString: DateToString,
    private _pollService: PollService,
    private _createOptionService: CreateOptionDataService,
    private _router: Router,
    private _errorHandlerService: ErrorhandlerService
  ) {
  }

  ngOnInit() {
    this._activatedRoute.url
      .pipe(takeWhile(() => this._subscriptionState))
      .subscribe((urlSegment) => {
        if (urlSegment.length > 2) {
          this.routeId = urlSegment[2].path;
        } else {
          this.routeId = urlSegment[1].path;
        }
      });
    this.assemblyEventList =
      this._activatedRoute.snapshot.data['assemblyEvents'];
    this.eventRegisterEnd = this.assemblyEventList.length
      ? this.assemblyEventList[0].expirationDate
      : '';
    this.assemblyPoll = this._activatedRoute.snapshot.data['assemblyPoll'];
    this.origin = this._activatedRoute.snapshot.queryParams['origin'];
    this.currentUrl = this._router.url;
    this.previousUrl = this.parentUrl + '/skab';
    this.successUrl = this.parentUrl + '/kalendar/afstemninger';
    document.body.classList.add('overlay');
    this.memberId = TokenService.getMemberId()
      ? TokenService.getMemberId()
      : '';
    this.assemblyPollForm = this._formBuilder.group(
      {
        id: [''],
        eventId: [''],
        title: ['', [Validators.required, checkWhiteSpace, checkMaxLength]],
        imageUrl: ['', Validators.required],
        startDate: ['', Validators.required],
        endDate: ['', Validators.required],
        description: [
          '',
          [Validators.required, checkWhiteSpace, checkIfXinDescription]
        ],
        answers: this._formBuilder.array([]),
        electionEntries: [1, Validators.required],
        requirements: ['', [Validators.required, checkWhiteSpace]]
      },
      {
        validators: [
          checkIfEndDateIsLessThanStartDate,
          this._checkPollStart.bind(this)
        ]
      }
    );
    if (this.assemblyPoll) {
      this.editMode = true;
      this.isActive = this.assemblyPoll.active === 1;
      this.pollStarted = this.assemblyPoll.started === 1;
      this.pollEnded = this.assemblyPoll.ended === 1;
      this.hasVoted = this.assemblyPoll.totalVotes !== 0;
      this.parentMemberId = this.assemblyPoll.memberId
        ? this.assemblyPoll.memberId
        : '';
      this.canEdit = this.assemblyPoll.isParent;
      this.memberIdNotMatched = this.parentMemberId !== this.memberId;
      if (this.pollEnded || !this.isActive) {

        this.pageTitle = 'Afstemning';
        this._buildAssemblyPollResults();
        this.showResults = true;
      }
      this.pageTitle = 'Rediger afstemning';
      if (this.editMode) {
        if (this.origin === 'list') {
          this.previousUrl = '/bestyrelse/afstemninger/';
          this.successUrl = this.previousUrl;
        }
        if (this.origin !== 'list') {
          this.previousUrl =
            this.parentUrl + '/kalendar/forsamling-poll/' + this.routeId;
        }
      }
      this._buildEditForm();
    } else {
      this._setDefaultImgTitleDesc();
      for (let i = 0; i < 2; i++) {
        this.addAnswer('');
      }
      this._setDefaultTimeOnCreate();
      this._setElectionEntries();
      this._selectSpecialEvent();
    }
  }

  ngOnDestroy(): void {
    document.body.classList.remove('overlay');
    this._subscriptionState = false;
  }

  onSelect() {
    this.visible = !this.visible;
  }

  imageProcessing(e) {
    this.isProcessing = e;
  }

  getImagesUrl(images) {
    this.assemblyPollForm.patchValue({
      imageUrl: images.largeImage
    });
  }

  initAnswer(answer?: string): FormGroup {
    return this._formBuilder.group({
      answer: [answer, [Validators.required, checkWhiteSpace]]
    });
  }

  addAnswer(answer: string) {
    const answers = this.assemblyPollForm.controls['answers'] as FormArray;
    const aa = this.initAnswer(answer);
    answers.push(aa);
    this.answerCount += 1;
  }

  removeAnswer(i: number) {
    const answer = this.assemblyPollForm.controls['answers'] as FormArray;
    answer.removeAt(i);
    this.answerCount -= 1;
  }

  deleteAssemblyPoll(id: string) {
    if (!this.editMode) {
      this.back();
    }
    if (this.editMode) {
      this._deleteAssemblyPoll(id, 'delete')
        .pipe(takeWhile(() => this._subscriptionState))
        .subscribe({
          next: () => {
            NotificationsService.notify(
              'Afstemning blev slettet',
              'success',
              'top'
            );
            this._success();
          },
          error: (err) => this._handleError(err)
        });
    }
  }

  downloadVotesAndUserList() {
    const id = this.assemblyPoll.parentId
      ? this.assemblyPoll.parentId
      : this.assemblyPoll.id;
    this._pollService
      .getAssemblyVotesOrUsersList(id)
      .pipe(
        take(1),
        mergeMap((file) => this.loadFile(file))
      )
      .subscribe({
        next: () => this.download(),
        error: (err) => this._handleError(err)
      });
  }

  private loadFile(file) {
    this.downloadFile.file = file;
    return FileReaderService.readFile(file, file['type']);
  }

  private download() {
    const id = this.assemblyPoll.parentId
      ? this.assemblyPoll.parentId
      : this.assemblyPoll.id;
    FileReaderService.downloadFile(
      this.downloadFile,
      'Poll_' + id + '_' + new Date().getTime()
    );
  }

  showOverlayMsg() {
    this.showMsg = !this.showMsg;
  }

  // go back button using setPreviousUrl
  back() {
    this._router.navigate([this.previousUrl]).then();
  }

  save() {
    if (!this._isValid()) {
      return;
    }

    if (!this.isProcessing) {
      this.isProcessing = true;
      this.assemblyPollForm.patchValue({
        startDate: this._dateToString.transform(
          this.assemblyPollForm.value.startDate
        ),
        endDate: this._dateToString.transform(
          this.assemblyPollForm.value.endDate
        )
      });
      this.assemblyPollForm.value.electionEntries =
        this.assemblyPollForm.value.electionEntries
          .toString()
          .replace(' poster', '');
      this._pollService
        .saveAssemblyPoll(this.assemblyPollForm.value, this.editMode)
        .pipe(takeWhile(() => this._subscriptionState))
        .subscribe({
          next: () => {
            const notificationMessage = this.editMode
              ? 'Afstemning er opdateret'
              : 'Afstemningen er oprettet';
            NotificationsService.notify(notificationMessage, 'success', 'top');
            this._success();
          },
          error: (err) => {
            if (err.code === 600) {
              const errMsg =
                'Der er ingen generalforsamling, så afstemning kan ikke oprettes.';
              NotificationsService.notify(errMsg, 'error', 'top');
              this.isProcessing = false;
            } else {
              this._handleError(err);
            }
          }
        });
    }
  }

  // ** Prepare Form Data and check Validation ** //
  // @return [boolean] => return false if form is invalid
  private _isValid() {
    if (this.assemblyPollForm.status === 'INVALID') {
      this._handleError('');
      return false;
    }
    return true;
  }

  private _buildEditForm() {
    const answers = this.assemblyPoll.answers;
    for (const answer of answers) {
      if (answer.id !== 0) {
        this.addAnswer(answer.answer);
      }
    }
    this.assemblyPollForm.patchValue(this.assemblyPoll);
    this.canEdit = this.assemblyPoll.isParent;
    this.imageUrl = {
      largeImage: this.assemblyPoll.imageUrl,
      thumbnail: this.assemblyPoll.imageUrl
    };
    this.hasImage = true;
    if (!this.canEdit || this.pollStarted && this.hasVoted) {
      this.assemblyPollForm.disable();
    }
  }

  private _selectSpecialEvent() {
    this.assemblyEventId = this.assemblyEventList.length
      ? this.assemblyEventList[0].id
      : '';
    this.assemblyPollForm.patchValue({
      eventId: this.assemblyEventId
    });
  }

  private _deleteAssemblyPoll(id: string, type: string) {
    return this._pollService.deleteAssemblyPoll(id, type);
  }

  private _buildAssemblyPollResults() {
    const answers = this.assemblyPoll.answers;
    for (let i = 0; i < answers.length; i++) {
      answers[i].percentage = parseFloat(
        (
          (parseInt(answers[i].votes, 10) / this.assemblyPoll.totalVotes) *
          100
        ).toString()
      ).toFixed(0);
      answers[i].shade = this.colors[i];
    }
  }

  private _setDefaultImgTitleDesc() {
    const imgUrl = `${environment.mainurl}/events/images/email.png`;
    const desc =
      'Hvis du er tilmeldt generalforsamlingen, kan du nu være med til at vælge, hvem der skal sidde i bestyrelsen i de næste år. \n\n' +
      '[Skriv mere om valgmulighederne]';
    const requirements =
      'Medlemmer har stemmeret efter 3 måneders medlemsskab. \n\n' +
      'Jeg bekræfter, at jeg har været medlem af brugsforeningen i mindst 3 måneder.';
    this.imageUrl = {
      largeImage: imgUrl,
      thumbnail: imgUrl
    };
    this.hasImage = true;
    this.assemblyPollForm.patchValue({
      imageUrl: imgUrl,
      title: 'Valg til bestyrelsen er åbent',
      description: desc,
      requirements
    });
  }

  private _setDefaultTimeOnCreate() {
    const startDate = DateFormatService.timeIntervalFormat(
      moment().add(3, 'hours')
    );
    const endDate = moment(startDate).add(1, 'day');
    const currForm = this.assemblyPollForm;
    currForm.patchValue({
      startDate: startDate,
      endDate: endDate
    });
  }

  private _setElectionEntries() {
    this.assemblyPollForm.patchValue({
      electionEntries: '1 poster'
    });
  }

  private _setupDateTime(inst: DatepickerBase) {
    if (!this.editMode) {
      // Set Start Date at Today time 8:00
      if (
        inst._el.id === 'startDate' &&
        !this.assemblyPollForm.value.startDate
      ) {
        this.assemblyPollForm.patchValue({
          startDate: moment().hour(8).minute(0).toDate()
        });
      }

      // EndDate picker clicked
      // If no Startdate set it date to Today time 20:00
      if (
        inst._el.id === 'expiry' &&
        !this.assemblyPollForm.value.endDate &&
        !this.assemblyPollForm.value.startDate
      ) {
        this.assemblyPollForm.patchValue({
          endDate: moment().hour(20).minute(0).toDate()
        });
      }

      // EndDate picker clicked
      // If Startdate set it to StartDate and time at 20:00
      if (
        inst._el.id === 'expiry' &&
        this.assemblyPollForm.value.startDate &&
        !this.assemblyPollForm.value.endDate
      ) {
        const startDate = moment(this.assemblyPollForm.value.startDate);
        this.assemblyPollForm.patchValue({
          endDate: startDate.hour(20).minute(0).toDate()
        });
      }
    }
  }

  private _initDurationPopup(inst: DatepickerBase) {
    if (!this.editMode) {
      const elementID = inst._el.id;
      if (elementID === 'startdate' || elementID === 'expiry') {
        const startDate = moment(this.assemblyPollForm.value.startDate);
        const endDate = moment(this.assemblyPollForm.value.endDate);
        if (endDate.diff(startDate, 'minutes') > 2880) {
          this.showDurationPopup = true;
        }
      }
    }
  }

  private _success(): void {
    this._createOptionService.clearCreateOption();
    this.formSaved = true;
    this.isProcessing = false;
    this._router.navigate([this.successUrl]).then();
  }

  private _handleError(error): void {
    this.isProcessing = false;
    this._errorHandlerService.handleError(error || { code: -400 }, 'poll');
  }
}
